import { matchPath } from 'react-router-dom';
import {
  ROLE,
  getRoleCodes,
  hasRole,
  isRegionalCoordinator,
} from '../../../utils/constants/roles';

/* ------------------------------------------------------------------ *
 * Ветки навбара = ровно те же 5 веток, что были в if/else внутри
 * useEffect: landing / SSA / (SA|*RC без TRADMIN) / (TRADMIN+SA) / else.
 * ------------------------------------------------------------------ */
export const BRANCH = {
  LANDING: 'landing',
  SSA: 'ssa',
  SA: 'sa',
  RC: 'rc',
  TRADMIN: 'tradmin', // TRADMIN + SA
  SCHOOL: 'school', // VP и всё, что провалилось в else
};

// *RC видел вкладку «Результаты», но роут /admin/results (App.js) пускает
// только TRADMIN|SSA|SA и редиректит остальных на /applicants — вкладка
// мёртвая, поэтому убрана. Поставить true -> поведение станет прежним.
const SHOW_ADMIN_RESULTS_FOR_RC = false;

/* ------------------------------------------------------------------ *
 * Секции. Секция БЕЗ подписи здесь никогда не сворачивается в меню —
 * её пункты всегда плоские вкладки. Секция, в которой после фильтрации
 * по ветке остался 1 пункт, тоже становится плоской вкладкой:
 * одинокое меню хуже ссылки.
 *
 * TODO i18n: перевести эти подписи в translations.nav.* нельзя, пока
 * fallbackLng = 'kk', казахских переводов админки нет, а переключателя
 * языка в админском NavBar нет — админ с navigator.language !== 'ru'
 * увидел бы казахский и не смог бы вернуть русский.
 * ------------------------------------------------------------------ */
export const SECTION_LABELS = {
  students: 'Ученики',
  registration: 'Регистрация',
  exams: 'Экзамены',
  admin: 'Администрирование',
  intake: 'Приём',
  school: 'Школа',
  schoolStats: 'Статистика',
};

// Порядок верхнего уровня. Псевдо-секции (top*, без подписи) — плоские вкладки.
const ADMIN_SECTION_ORDER = [
  'top',
  'students',
  'topTransfer',
  'registration',
  'exams',
  'admin',
];

const SCHOOL_SECTION_ORDER = [
  'topResults',
  'intake',
  'topTransfer',
  'school',
  'schoolStats',
  'topForms',
];

const B = BRANCH;
const ALL_ADMIN = [B.SSA, B.SA, B.RC, B.TRADMIN];

/* ------------------------------------------------------------------ *
 * Пункты админских веток. Порядок внутри секции = порядок объявления.
 * `shortName` используется ТОЛЬКО когда пункт рендерится плоской
 * вкладкой верхнего уровня (секция схлопнулась) — чтобы длинная подпись
 * не распирала ряд у SA/*RC. Внутри меню и в <h1> всегда `name`.
 * ------------------------------------------------------------------ */
const ADMIN_ITEMS = [
  {
    id: 'adminResults',
    name: 'Результаты',
    href: '/admin/results',
    isNew: true,
    section: 'top',
    branches: SHOW_ADMIN_RESULTS_FOR_RC ? ALL_ADMIN : [B.SSA, B.SA, B.TRADMIN],
  },

  // Ученики — единственные страницы, где строка = человек (ИИН / ФИО / заявление)
  {
    id: 'students',
    name: 'Принятые ученики',
    href: '/students',
    section: 'students',
    branches: [B.SSA, B.TRADMIN],
  },
  {
    id: 'acceptanceStats',
    name: 'Статистика сбора документов',
    shortName: 'Сбор документов',
    href: '/school-acceptance-stats',
    isNew: true,
    section: 'students',
    branches: ALL_ADMIN,
  },
  {
    id: 'transfer',
    name: 'Трансфер',
    href: '/transfer',
    section: 'students',
    branches: [B.SSA],
  },

  // Для TRADMIN+SA «Трансфер» — профильная страница, держим её плоской
  {
    id: 'transferPinned',
    name: 'Трансфер',
    href: '/transfer',
    section: 'topTransfer',
    branches: [B.TRADMIN],
  },

  // Регистрация — воронка заявок. «Цели школ» здесь как план-знаменатель
  // воронки, «Результаты опроса» — опрос при регистрации (своя форма на
  // каждый год экзамена), «Формы» — редактор этих опросов (SSA и TRADMIN).
  {
    id: 'registrationStats',
    name: 'Статистика регистраций школ',
    href: '/school-registration-statistics',
    section: 'registration',
    branches: ALL_ADMIN,
  },
  {
    id: 'registrationComparison',
    name: 'Статистика сравнения регистраций',
    href: '/registration-comparison-statistics',
    isNew: true,
    section: 'registration',
    branches: ALL_ADMIN,
  },
  {
    id: 'registrationTimeline',
    name: 'Динамика регистраций',
    href: '/registration-timeline',
    isNew: true,
    section: 'registration',
    branches: [B.SSA, B.SA, B.TRADMIN], // роут пускает только SA|SSA
  },
  {
    id: 'schoolGoals',
    name: 'Цели школ',
    href: '/admin/school_goals',
    isNew: true,
    section: 'registration',
    branches: [B.SSA, B.TRADMIN],
  },
  {
    id: 'formStats',
    name: 'Результаты опроса',
    href: '/form-stats',
    isNew: true,
    section: 'registration',
    branches: ALL_ADMIN,
  },
  {
    id: 'forms',
    name: 'Формы',
    href: '/forms',
    isNew: true,
    section: 'registration',
    branches: [B.SSA, B.TRADMIN], // роут пускает только SSA|TRADMIN
  },

  // Экзамены — всё вокруг самого экзамена (тур / дата / кабинет / балл) плюс
  // управление онлайн-экзаменами: иначе пользователь откроет «Экзамены»,
  // не найдёт там экзаменов и уйдёт искать в «Администрирование».
  {
    id: 'examStats',
    name: 'Статистика экзаменов школ',
    href: '/school-exam-statistics',
    section: 'exams',
    branches: ALL_ADMIN,
  },
  {
    id: 'examTimeStats',
    name: 'Статистика времени экзаменов школ',
    href: '/school-exam-time-statistics',
    section: 'exams',
    branches: ALL_ADMIN,
  },
  {
    id: 'classroomStats',
    name: 'Статистика кабинетов',
    href: '/school-classroom-statistics',
    section: 'exams',
    branches: ALL_ADMIN,
  },
  {
    id: 'passScoreStats',
    name: 'Статистика проходных баллов школ',
    href: '/school-pass-score-statistics',
    section: 'exams',
    branches: ALL_ADMIN,
  },
  {
    id: 'adminExams',
    name: 'Онлайн экзамены',
    href: '/admin/exams',
    section: 'exams',
    branches: [B.SSA],
  },

  // Администрирование — настройки и справочники
  {
    id: 'settings',
    name: 'Настройки',
    href: '/admin/settings',
    section: 'admin',
    branches: [B.SSA],
  },
  {
    id: 'extraStudents',
    name: 'Настройка доп. учеников',
    href: '/admin/extra-allowed-students-settings',
    section: 'admin',
    branches: [B.SSA],
  },
  {
    id: 'districts',
    name: 'Районы',
    href: '/districts',
    section: 'admin',
    branches: ALL_ADMIN,
  },
];

/* ------------------------------------------------------------------ *
 * Школьная ветка: другой словарь (Приём / Заявки / Моя школа) и другие
 * подписи у общих роутов («Районы / Экзам центры» вместо «Районы»,
 * /results вместо /admin/results), поэтому отдельная таблица, а не
 * механизм переопределений.
 * ------------------------------------------------------------------ */
const SCHOOL_ITEMS = [
  {
    id: 'schoolResults',
    name: 'Результаты',
    href: '/results',
    section: 'topResults',
  },
  {
    id: 'accepted',
    name: 'Принять документы',
    href: '/accepted',
    section: 'intake',
  },
  { id: 'applicants', name: 'Заявки', href: '/applicants', section: 'intake' },
  {
    id: 'schoolStudents',
    name: 'Принятые ученики',
    href: '/students',
    section: 'intake',
  },
  {
    id: 'districtRegistration',
    name: 'Оффлайн регистрация района',
    href: '/district-registration',
    section: 'intake',
  },
  {
    id: 'schoolTransfer',
    name: 'Трансфер',
    href: '/transfer',
    section: 'topTransfer',
    // Сезонный гейт, перенесён закомментированным из старого NavBar:
    // hidden: (user) => !['016', '017'].includes(user?.school?.school_id),
  },
  { id: 'mySchool', name: 'Моя школа', href: '/my-school', section: 'school' },
  {
    id: 'schoolDistricts',
    name: 'Районы / Экзам центры',
    href: '/districts',
    section: 'school',
  },
  {
    id: 'schoolClassroomStats',
    name: 'Статистика кабинетов',
    href: '/school-classroom-statistics',
    section: 'school',
  },
  {
    id: 'schoolRegComparison',
    name: 'Статистика сравнения регистраций',
    href: '/registration-comparison-statistics',
    isNew: true,
    section: 'schoolStats',
  },
  {
    id: 'schoolFormStats',
    name: 'Результаты опроса',
    href: '/form-stats',
    isNew: true,
    section: 'schoolStats',
  },
  {
    id: 'examinationsStats',
    name: 'Пробные экзамены',
    href: '/examinations-stats',
    section: 'schoolStats',
  },
  // TRADMIN без SA попадает в школьную ветку, а роут /forms его пускает —
  // поэтому «Формы» здесь есть, но только у него (у VP вкладки нет).
  {
    id: 'schoolForms',
    name: 'Формы',
    href: '/forms',
    isNew: true,
    section: 'topForms',
    hidden: (user) => !hasRole(user, ROLE.TRADMIN),
  },
];

const landingItems = (t) => [
  {
    id: 'welcomeAdmission',
    name: t('welcome.admission'),
    href: '#',
    section: 'top',
  },
  { id: 'welcomeRules', name: t('welcome.rules'), href: '#', section: 'top' },
  {
    id: 'welcomeContacts',
    name: t('welcome.contacts'),
    href: '#',
    section: 'top',
  },
  { id: 'welcomeFaq', name: t('welcome.faq'), href: '#', section: 'top' },
];

/* ------------------------------------------------------------------ *
 * Выбор ветки — побайтово те же условия, что в старом if/else.
 * ------------------------------------------------------------------ */
export function resolveBranch(user, pathname) {
  if (pathname === '/') return B.LANDING;

  const codes = getRoleCodes(user);
  const has = (code) => codes.includes(code);
  const hasRC = codes.some(isRegionalCoordinator);

  if (has(ROLE.SSA)) return B.SSA;
  if ((has(ROLE.SA) || hasRC) && !has(ROLE.TRADMIN)) {
    return has(ROLE.SA) ? B.SA : B.RC;
  }
  if (has(ROLE.TRADMIN) && has(ROLE.SA)) return B.TRADMIN;

  // ВНИМАНИЕ: TRADMIN в одиночку и TRADMIN + *RC (без SA) попадают сюда и
  // получают школьное меню. Это ПРЕЖНЕЕ поведение, сохранено намеренно.
  return B.SCHOOL;
}

/* ------------------------------------------------------------------ *
 * Чистая сборка дерева.
 * ИНВАРИАНТ: flattenNav(buildNav(items, order)) — перестановка items.
 * ------------------------------------------------------------------ */
export function buildNav(items, sectionOrder) {
  const bySection = new Map();
  items.forEach((item) => {
    if (!bySection.has(item.section)) bySection.set(item.section, []);
    bySection.get(item.section).push({ type: 'link', ...item });
  });

  const nodes = [];
  sectionOrder.forEach((sectionId) => {
    const children = bySection.get(sectionId);
    if (!children?.length) return;
    bySection.delete(sectionId);

    const label = SECTION_LABELS[sectionId];
    if (!label || children.length < 2) {
      children.forEach((child) => nodes.push(child));
      return;
    }
    nodes.push({
      type: 'group',
      id: sectionId,
      name: label,
      children,
      hasNew: children.some((child) => child.isNew),
    });
  });

  // Fail-open: пункт с секцией, которой нет в порядке, всё равно рендерится
  // плоской вкладкой, а не исчезает молча.
  bySection.forEach((children) =>
    children.forEach((child) => nodes.push(child)),
  );

  return nodes;
}

/** Единственная точка входа для NavBar. */
export function resolveNavigation(user, t, pathname) {
  const branch = resolveBranch(user, pathname);

  if (branch === B.LANDING) return buildNav(landingItems(t), ['top']);

  if (branch === B.SCHOOL) {
    const items = SCHOOL_ITEMS.filter((item) => !item.hidden?.(user));
    return buildNav(items, SCHOOL_SECTION_ORDER);
  }

  const items = ADMIN_ITEMS.filter(
    (item) => item.branches.includes(branch) && !item.hidden?.(user),
  );
  return buildNav(items, ADMIN_SECTION_ORDER);
}

/* ------------------------------------------------------------------ *
 * Активность и <h1>
 * ------------------------------------------------------------------ */

/**
 * Посегментное совпадение через matchPath самого react-router-dom — та же
 * семантика, что у NavLink isActive, поэтому подсветка вкладки, подсветка
 * группы и <h1> не могут разойтись. Заменяет pathname.includes(href),
 * из-за которого /results матчился на /admin/results.
 */
export function isHrefActive(pathname, href) {
  if (!pathname || !href || href === '#') return false;
  return matchPath({ path: href, end: false }, pathname) !== null;
}

export function flattenNav(nodes) {
  return nodes.flatMap((node) =>
    node.type === 'group' ? node.children : [node],
  );
}

/** Побеждает самый длинный совпавший href: /admin/results/upload -> «Результаты». */
export function findCurrentItem(nodes, pathname) {
  return flattenNav(nodes)
    .filter((item) => isHrefActive(pathname, item.href))
    .sort((a, b) => b.href.length - a.href.length)[0];
}
